export type MessageParamFunction = (...params: (string | number)[]) => string;
export type MessageType = string | MessageParamFunction;

export const MessageCodes = {
  GENERIC_ERROR: 'FE0001',
  NETWORK_ERROR: 'FE0002',
  BAD_REQUEST: 'FE0003',
  UNAUTHORIZED: 'FE0004',
  FORBIDDEN: 'FE0005',
  NOT_FOUND: 'FE0006',
  CONFLICT: 'FE0007',
  UNPROCESSABLE: 'FE0008',
  SERVICE_UNAVAILABLE: 'FE0009',
  GATEWAY_TIMEOUT: 'FE0010',
  INSUFFICIENT_STORAGE: 'FE0011',
  LARGE_UPLOAD_STARTED: 'FE0012',
  UNEXPECTED_ERROR: 'FE0013',
  ACTION_SUCCESS: 'FE0100',
  ACTION_FAILED: 'FE0101',
  FETCH_FAILED: 'FE0102',
  FETCH_FAILED_WITH_REASON: 'FE0103',
  FETCH_FAILED_WITH_DETAIL: 'FE0104',
  SESSION_EXPIRED: 'FE0105',
  FILE_DOWNLOAD_READY: 'FE0107',
  GATE_IN_RECEIVED: 'FE0108',
  NOTIFY_REGISTER_FAILED: 'FE0109',
  UPLOAD_SUCCESS: 'FE0110',
  UPLOAD_FAILED: 'FE0113',
  DOWNLOAD_FAILED: 'FE0114',
  KIND_REQUIRED_MISSING: 'FE0117',
  FILE_REQUIRED: 'FE0118',
  KIND_REQUIRED_SELECT: 'FE0119',
  JOB_REGISTER_FAILED: 'FE0120',
  UPLOAD_FAILED_WITH_REASON: 'FE0122',
  NETWORK_ERROR_LABEL: 'FE0123',
  TEMPLATE_FETCH_SUCCESS: 'FE0124',
  TEMPLATE_FETCH_FAILED: 'FE0126',
  COMMUNICATION_ERROR_UNKNOWN: 'FE0127',
  LOGIN_LOCKED: 'FE0128',
  ROLE_DELETE_BLOCKED: 'FE0129',
  USER_PROFILE_UPDATED: 'FE0130',
  CACHE_TEMPLATE_NOT_FOUND: 'FE0131',
  CACHE_EXPIRED: 'FE0132',
  TEMPLATE_SHEETS_COLUMNS_EMPTY: 'FE0133',
  TEMPLATE_UNKNOWN_FORMAT: 'FE0134',
  UNSUPPORTED_FILE_TYPE: 'FE0135',
  INVALID_YAML_DATA: 'FE0136',
  MANIFEST_INVALID_NUMBER10_PAPER: 'FE0137',
  MANIFEST_INVALID_NUMBER10_ELECTRONIC: 'FE0138',
  MANIFEST_INVALID_NUMBER11: 'FE0139',
  MANIFEST_INVALID_CHECK_TYPE: 'FE0140',
  WEBSOCKET_CLIENT_REQUIRED: 'FE0141',
  WEBSOCKET_CONTEXT_REQUIRED: 'FE0142',
  STOMP_CONNECTION_FAILED: 'FE0143',
  FILE_SIZE_LIMIT: 'FE0144',
  ALL_FIELDS_REQUIRED: 'FE0145',
  DATA_NOT_FOUND: 'FE0146',
  SERVER_ERROR: 'FE0147',
  API_ERROR_500: 'FE0148',
  OIDC_AUTH_URL_FAILED: 'FE0149',
  OIDC_TOKEN_EXCHANGE_FAILED: 'FE0150',
  FORCED_PARSE_ERROR: 'FE0151',
  SUCCESS_WITH_DETAIL: 'FE0152',
  COMPLETE: 'FE0154',
  BULK_EXPORT_INVALID_ID: 'FE0155',
  SUBMIT_FAILED: 'FE0156',
  CALLBACK_MISSING_PARAMS: 'FE0157',
  AUTH_ERROR_GENERIC: 'FE0158',
  CALLBACK_COMMUNICATION_ERROR: 'FE0159',
  ERROR_OCCURRED_WITH_DETAIL: 'FE0161',
  CACHE_KEY_NOT_FOUND: 'FE0162',
  CACHE_UPDATE_SUCCESS: 'FE0163',
  FILE_CREATE_ERROR_WITH_DETAIL: 'FE0164',
  CSV_HEADER_NOT_FOUND: 'FE0200',
  CSV_MAX_ROW_EXCEEDED: 'FE0201',
  CSV_HEADER_MISSING: 'FE0202',
  CSV_HEADER_EXTRA: 'FE0203',
  FILE_READ_ERROR: 'FE0204',
  CSV_EMPTY_ROW: 'FE0205',
  CSV_REQUIRED: 'FE0206',
  CSV_INVALID_TYPE: 'FE0207',
  CSV_ENUM_MISMATCH: 'FE0208',
  CSV_INVALID_VALUE: 'FE0209',
  CSV_INVALID_FORMAT: 'FE0210',
  EXCEL_SHEET_NOT_FOUND: 'FE0211',
  EXCEL_HEADER_MISMATCH: 'FE0212',
  EXCEL_MAX_ROW_EXCEEDED: 'FE0213',
  EXCEL_PARSE_ERROR: 'FE0214',
  EXCEL_READ_ERROR: 'FE0215',
  EXCEL_EMPTY_ROW: 'FE0216',
  EXCEL_REQUIRED: 'FE0217',
  EXCEL_INVALID_REGEX: 'FE0218',
  EXCEL_EXCEED_MAX_LENGTH: 'FE0219',
  EXCEL_INVALID_ENUM: 'FE0220',
  EXCEL_INVALID_PATTERN: 'FE0221',
  EXCEL_INVALID_VALUE: 'FE0222',
  EXCEL_INVALID_NUMBER: 'FE0223',
  EXCEL_INVALID_DATE: 'FE0224',
  DATE_INVALID: 'FE0225',
} as const;

export const MessageIds: Record<string, MessageType> = {
  E008: (param) => `${param}は必須項目です。`,
  FE0001: 'エラーが発生しました',
  FE0002: 'ネットワークに問題があります。接続を確認してください。',
  FE0003: '入力内容に誤りがあります。再度ご確認ください。',
  FE0004: 'ログインに失敗しました。ユーザーIDやパスワードをご確認ください。',
  FE0005: 'この操作を行う権限がありません。',
  FE0006: 'お探しのページが見つかりません。URLをご確認ください。',
  FE0007: '同じデータが既に存在しています。内容を見直してください。',
  FE0008: '入力された情報に不整合があります。',
  FE0009: 'ネットワーク接続が不安定です。リトライします。',
  FE0010: '処理に時間がかかっています。',
  FE0011: 'サーバーのストレージ容量が不足しています。',
  FE0012: '大容量データのアップロードを開始しました。',
  FE0013: '予期せぬエラーが発生しました。',
  FE0100: (param1) => `${param1}しました`,
  FE0101: (param1) => `${param1}に失敗しました`,
  FE0102: (param1) => `${param1}の取得に失敗しました`,
  FE0103: (param1, param2) => `${param1}の取得に失敗しました。${param2}`,
  FE0104: (param1, param2) => `${param1}の取得に失敗しました: ${param2}`,
  FE0105: 'セッションが切れました。再ログインしてください',
  FE0107: 'ファイルダウンロードの準備が完了しました。',
  FE0108: (param1) => `GATE_IN 通知を受信しました: ${param1}`,
  FE0109: '通知登録に失敗しました',
  FE0110: 'アップロードが完了しました。',
  FE0113: 'アップロードに失敗しました',
  FE0114: 'ダウンロードに失敗しました。',
  FE0117: 'ファイル種別（kind）が指定されていません。',
  FE0118: 'ファイルを選択してください。',
  FE0119: 'ファイル種別（kind）を選択してください。',
  FE0120: '帳票出力ジョブの登録に失敗しました。',
  FE0122: (param1) => `アップロードに失敗しました: ${param1}`,
  FE0123: '通信エラー',
  FE0124: 'テンプレートの取得が完了しました。',
  FE0126: 'テンプレート取得に失敗しました',
  FE0127: '不明な通信エラーが発生しました。',
  FE0128: 'ログイン試行回数が上限に達しました。管理者に連絡してください。',
  FE0129: 'このロールはユーザーに割り当てられているため、削除できません。',
  FE0130: 'ユーザー情報が更新されました',
  FE0131: '/キャッシュに templateId .* が存在しません/',
  FE0132: '/キャッシュは期限切れです/',
  FE0133: 'sheets[0].columns が空か未定義です',
  FE0134: '不明なテンプレート形式です（columnsもsheetsも存在しません）',
  FE0135: (param1) => `未対応のファイル種別です: ${param1}`,
  FE0136: 'YAMLデータが不正です',
  FE0137: '紙マニフェストの番号（10桁）が不正です',
  FE0138: '電子マニフェストの番号（10桁）が不正です',
  FE0139: 'マニフェスト番号は11桁の文字列で入力してください',
  FE0140: 'チェック種別が不正です',
  FE0141: 'useWebSocketClient は WebSocketProvider 内で使用してください',
  FE0142: 'useWebSocketContext は WebSocketProvider 内で使用してください',
  FE0143: 'STOMP 接続に失敗しました',
  FE0144: (param1) => `ファイルサイズは${param1}MB以下にしてください`,
  FE0145: 'すべての項目を入力してください',
  FE0146: 'データが見つかりません',
  FE0147: 'サーバーエラー',
  FE0148: 'API エラー 500 発生',
  FE0149: '認可URLの取得に失敗しました',
  FE0150: 'トークンの取得に失敗しました',
  FE0151: '解析エラー（強制）',
  FE0152: (param1) => `処理が完了しました: ${param1}`,
  FE0154: '完了しました。',
  FE0155: 'ファイル名から有効なIDを取得できませんでした。',
  FE0156: '送信に失敗しました。',
  FE0157: '認可コードまたは state が存在しません。',
  FE0158: '認証エラーが発生しました。',
  FE0159: 'サーバーとの通信中にエラーが発生しました。',
  FE0161: (param1) => `エラーが発生しました: ${param1}`,
  FE0162: 'キャッシュの更新に紐づくkeyが存在しません',
  FE0163: 'キャッシュの更新に成功しました。',
  FE0164: (param1) => `ファイル作成エラー: ${param1}`,
  FE0200: 'ヘッダー行が見つかりません（行数: {count}）',
  FE0201: '行数上限（{max}行）を超えています（{actual}行）',
  FE0202: '欠落ヘッダー: {missing}',
  FE0203: '余分なヘッダー: {extra}',
  FE0204: 'ファイル読み取りエラー',
  FE0205: '空行が検出されました: {row} 行目',
  FE0206: '必須項目 \'{key}\' が未入力です: {row} 行目',
  FE0207: '項目 \'{key}\' の型が不正です: {row} 行目',
  FE0208: '項目 \'{key}\' の値 \'{value}\' は許容されていません: {row} 行目',
  FE0209: '項目 \'{key}\' の値が無効です: {row} 行目',
  FE0210: '項目 \'{key}\' の形式エラー: {row} 行目',
  FE0211: '指定されたシート「{sheet}」が存在しないため、取込できません。',
  FE0212: 'ヘッダの内容が不正です、もう一度Excelファイルを取得してください。',
  FE0213: '取込可能レコード件数({max}件)を超過しているため、取込できません。（{actual}件）',
  FE0214: 'Excelファイル解析中にエラーが発生しました',
  FE0215: 'Excelファイルの読み取りエラー',
  FE0216: '空行が検出されました。(行番号：{row})',
  FE0217: '{field}は必須項目です。{row}',
  FE0218: '{field}の正規表現パターンが不正なため、取込できません。{row}',
  FE0219: '{field}の文字数が上限({max}文字)を超えているため、取込できません。{row}',
  FE0220: '{field}の設定値が不正のため、取込できません。{row}',
  FE0221: '{field}の形式が不正のため、取込できません。{row}',
  FE0222: '{field}の値が無効のため、取込できません。{row}',
  FE0223: '{field}の型が不正のため、取込できません。{row}',
  FE0224: '{field}が有効な日付ではないため、取込できません。{row}',
  FE0225: '日付の形式が正しくありません。',
};

export const getMessage = (code: string, ...params: (string | number)[]) => {
  const entry = MessageIds[code];
  if (!entry) return code;
  return typeof entry === 'function' ? entry(...params) : entry;
};
